// Batch document export.
//
// Two modes, and which one is live is decided by whether anything is ticked:
//
//   SELECTION — rows ticked in the list. Sends `ids`, which offerListFilter()
//   turns into an `_id: { $in: [...] }` still composed with the caller's
//   entitlement scope, so ticking an id outside your scope matches nothing
//   rather than reaching across brokerages.
//
//   FILTER — nothing ticked. Sends the filter bar's current state, so the
//   ZIP is the list you are looking at.
//
// The confirmation names the count either way, because the whole risk of this
// button is that the user thinks it means something narrower than it does.
import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, BtnSecondary } from '../regureStyles';
import PermissionGate from '../../components/PermissionGate';

// Same shape the rest of this screen uses — REACT_APP_BASE_URL already
// includes /api/v1.
const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Mirrors BATCH_DOCUMENT_CAP in backend/controllers/offerController.js. The
// server is authoritative and refuses anything above it with a 400; this copy
// exists so the user is told BEFORE spending a click, not after.
const BATCH_DOCUMENT_CAP = 50;

// MEASURED: 194 documents in 119 seconds against the live ARGOS book.
const SECONDS_PER_DOCUMENT = 0.61;

export default function GenerateAllButton({ filters, total, selectedIds, onExported }) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const selectedCount = selectedIds ? selectedIds.size : 0;
  const usingSelection = selectedCount > 0;
  const count = usingSelection ? selectedCount : total;
  const overCap = count > BATCH_DOCUMENT_CAP;
  const eta = Math.max(1, Math.round(count * SECONDS_PER_DOCUMENT));

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = usingSelection
        ? { ids: Array.from(selectedIds) }
        : { ...filters };
      // `responseType: 'blob'` or the ZIP arrives as mangled text.
      const resp = await axios.post(
        `${BASE_URL}/offers/generate-batch-documents`,
        { filters: payload },
        // The cap is 50 documents ≈ 31 seconds, so this ceiling is now far
        // more headroom than the export can use. It stays generous because
        // the cost of being wrong is a truncated ZIP: the 200 and the ZIP
        // headers go out before the first render, so an abort mid-stream
        // cannot be turned back into an error the user can read.
        //
        // The connection does not sit idle while it waits: the server
        // appends each PDF to the archive as it finishes and the bytes flow
        // immediately, which also keeps the proxy's read timeout from
        // firing mid-export.
        { withCredentials: true, responseType: 'blob', timeout: 600000 },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `offers-documents-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setAsking(false);
      if (onExported) onExported();
    } catch (err) {
      // The error body is a Blob too, because responseType applies to every
      // response including the 400s — read it back as text or the user gets
      // "[object Blob]" where the reason should be.
      let message = t('offers.list.generateAllFailed');
      try {
        const body = err?.response?.data;
        if (body instanceof Blob) {
          const parsed = JSON.parse(await body.text());
          if (parsed?.message) message = parsed.message;
        } else if (body?.message) {
          message = body.message;
        }
      } catch (_) { /* keep the generic message */ }
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const label = busy
    ? t('offers.list.generateAllBusy')
    : (usingSelection
      ? t('offers.list.generateSelected', { count: selectedCount })
      : t('offers.list.generateAll'));

  return (
    <PermissionGate resource='offers' action='update'>
      <span style={{ position: 'relative', display: 'inline-block' }}>
        <BtnSecondary onClick={() => { setError(null); setAsking(true); }} disabled={busy}>
          <Icon name='download' color={usingSelection ? S.burgundy : S.textMuted} size={15} />
          {' '}
          {label}
        </BtnSecondary>

        {asking && (
          // Inline, not window.confirm — a native dialog blocks the page and
          // cannot show the count in the user's own language.
          <div
            role='dialog'
            aria-label={t('offers.list.generateAll')}
            style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 40,
              width: 330, padding: 16, background: '#fff', textAlign: 'left',
              border: `1px solid ${S.borderLight}`, borderRadius: 12,
              boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
            }}
          >
            <div style={{ fontSize: 13.5, color: S.textPrimary, lineHeight: 1.5, marginBottom: 4 }}>
              {usingSelection
                ? t('offers.list.generateSelectedConfirm', { count: selectedCount })
                : t('offers.list.generateAllConfirm', { count: total })}
            </div>

            {overCap ? (
              // The server would refuse this. Say so here, with the number and
              // the two ways out, rather than letting the user spend a click
              // to receive a 400.
              <div style={{
                fontSize: 12, color: '#92400e', background: '#fffbeb',
                border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px',
                margin: '8px 0 10px', lineHeight: 1.45,
              }}
              >
                {t('offers.list.generateTooMany', { count, cap: BATCH_DOCUMENT_CAP })}
              </div>
            ) : (
              <div style={{ fontSize: 11.5, color: S.textMuted, lineHeight: 1.5, marginBottom: 12 }}>
                {t('offers.list.generateAllHint')}
                {' '}
                {t('offers.list.generateAllEta', { seconds: eta })}
              </div>
            )}

            {error && (
              <div style={{
                fontSize: 12, color: '#b42318', background: '#fef3f2',
                border: '1px solid #fecdca', borderRadius: 8, padding: '8px 10px',
                marginBottom: 10, lineHeight: 1.45,
              }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <BtnSecondary onClick={() => setAsking(false)} disabled={busy}>
                {t('common.cancel')}
              </BtnSecondary>
              {/* The confirming action is PRIMARY. Both buttons were
                  BtnSecondary — near-white on white with grey text — so the
                  one that starts the export read as the lesser of the two,
                  and the dialog as a whole looked ghosted. */}
              <BtnPrimary onClick={run} disabled={busy || overCap}>
                {busy ? t('offers.list.generateAllBusy') : t('offers.list.generateAllGo')}
              </BtnPrimary>
            </div>
          </div>
        )}
      </span>
    </PermissionGate>
  );
}
